import type { Asset, DepreciationResult, DepreciationRule } from "../types/imobi";

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function calculateVehicleElapsedMonths(asset: Asset, referenceDate: Date) {
  const baseYear = asset.manufactureYear && asset.modelYear
    ? Math.round((asset.manufactureYear + asset.modelYear) / 2)
    : asset.manufactureYear ?? asset.modelYear;

  if (!baseYear) {
    return null;
  }

  const start = new Date(baseYear, 0, 1);
  return Math.max(
    (referenceDate.getFullYear() - start.getFullYear()) * 12 + referenceDate.getMonth() - start.getMonth(),
    0
  );
}

function calculateMileageWeight(asset: Asset, elapsedMonths: number) {
  if (!asset.mileageKm || elapsedMonths <= 0) {
    return 1;
  }

  const elapsedYears = Math.max(elapsedMonths / 12, 0.25);
  const expectedMileage = elapsedYears * 15000;
  return clamp(asset.mileageKm / expectedMileage, 0.75, 1.35);
}

export function calculateLinearMonthlyDepreciation(asset: Asset, rule?: DepreciationRule, referenceDate = new Date()): DepreciationResult {
  const acquisitionValue = asset.acquisitionValue ?? 0;
  const usefulLifeYears = rule?.usefulLifeYears ?? asset.estimatedUsefulLifeYears ?? 0;
  const residualValue = acquisitionValue * ((rule?.residualValuePercent ?? 0) / 100);
  const usefulLifeMonths = Math.max(usefulLifeYears * 12, 1);
  const depreciableValue = Math.max(acquisitionValue - residualValue, 0);
  const start = asset.inUseDate ? new Date(asset.inUseDate) : asset.purchaseDate ? new Date(asset.purchaseDate) : referenceDate;
  const chronologicalElapsedMonths = Math.max(
    (referenceDate.getFullYear() - start.getFullYear()) * 12 + referenceDate.getMonth() - start.getMonth(),
    0
  );
  const vehicleElapsedMonths = calculateVehicleElapsedMonths(asset, referenceDate);
  const elapsedMonths = vehicleElapsedMonths ?? chronologicalElapsedMonths;
  const isNonDepreciable = !usefulLifeYears || !rule?.annualRate && rule?.usefulLifeYears === null;
  const monthlyDepreciation = isNonDepreciable ? 0 : depreciableValue / usefulLifeMonths;
  const mileageWeight = vehicleElapsedMonths === null ? 1 : calculateMileageWeight(asset, elapsedMonths);
  const accumulatedDepreciation = isNonDepreciable ? 0 : Math.min(monthlyDepreciation * elapsedMonths * mileageWeight, depreciableValue);

  return {
    depreciableValue,
    accumulatedDepreciation,
    monthlyDepreciation,
    currentBookValue: Math.max(acquisitionValue - accumulatedDepreciation, residualValue),
    residualValue,
    usefulLifeMonths: isNonDepreciable ? 0 : usefulLifeMonths,
    elapsedMonths: isNonDepreciable ? 0 : Math.min(elapsedMonths, usefulLifeMonths)
  };
}

export function findDepreciationRuleForAsset(asset: Asset, rules: DepreciationRule[]): DepreciationRule | undefined {
  return rules.find((rule) => rule.active && rule.id === asset.assetTypeId)
    ?? rules.find((rule) => rule.active && rule.assetTypeName.toLocaleLowerCase("pt-BR") === asset.assetTypeId.toLocaleLowerCase("pt-BR"));
}

export function buildDepreciationChartData(asset: Asset, rule?: DepreciationRule) {
  const result = calculateLinearMonthlyDepreciation(asset, rule);
  const acquisitionValue = asset.acquisitionValue ?? 0;
  const totalMonths = result.usefulLifeMonths || 0;
  const mileageWeight = calculateVehicleElapsedMonths(asset, new Date()) === null ? 1 : calculateMileageWeight(asset, Math.max(result.elapsedMonths, 1));

  if (!totalMonths || !acquisitionValue) {
    return [
      { label: "Atual", month: 0, bookValue: acquisitionValue, accumulatedDepreciation: 0 }
    ];
  }

  const step = Math.max(1, Math.ceil(totalMonths / 8));
  const points = [];
  for (let month = 0; month <= totalMonths; month += step) {
    const accumulatedDepreciation = Math.min(result.monthlyDepreciation * month * mileageWeight, result.depreciableValue);
    points.push({
      label: `${Math.floor(month / 12)}a ${month % 12}m`,
      month,
      bookValue: Math.max(acquisitionValue - accumulatedDepreciation, result.residualValue),
      accumulatedDepreciation
    });
  }

  if (points[points.length - 1]?.month !== totalMonths) {
    points.push({
      label: `${Math.floor(totalMonths / 12)}a ${totalMonths % 12}m`,
      month: totalMonths,
      bookValue: result.residualValue,
      accumulatedDepreciation: Math.min(result.monthlyDepreciation * totalMonths * mileageWeight, result.depreciableValue)
    });
  }

  return points;
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value).replace(/\u00a0/g, " ");
}
